/**
 * WANLE-2 · 携程玩乐：订单「未到」、每日名单、渠道中心凭据。
 * 契约：designs/mockups/channel-center/API_MAP-携程玩乐直连.md（tag ctrip-wanle-mockup-v1）⑥⑦。
 *   POST /api/v1/orders/{id}/ctrip-wanle:no-show · GET /api/v1/orders/ctrip-wanle-roster?date=&product_id=（&format=xlsx&full_phone=true 需 orders.ctrip_wanle_roster.export）
 *   PUT /api/v1/integrations/platforms/ctrip_wanle/wanle-credentials（只回「已配置」，明文不回显）
 */
import { useAuthStore } from "@/store/authStore";
import { api, apiConfig } from "./api";

export interface WanleRosterRow {
  order_id: string;
  order_no: string;
  ota_order_id: string;
  product_id: string;
  product_code: string;
  product_name: string;
  slot: string;
  name: string;
  pax: number;
  phone: string;
  remark: string;
  status: string;
}
export interface WanleRoster { date: string; product_id: string | null; can_export: boolean; total_orders: number; total_pax: number; rows: WanleRosterRow[] }

export interface WanleSandboxResult { ok: boolean; service: string; plu: string; http_status: number | null; result_code: string; result_message: string; sent_dates: string[] }

export const wanleApi = {
  noShow: (orderId: string): Promise<{ id: string; order_no: string; no_show_at: string | null; reported: boolean }> =>
    api(`/api/v1/orders/${orderId}/ctrip-wanle:no-show`, { method: "POST" }),
  roster: (date: string, productId?: string, fullPhone = false): Promise<WanleRoster> => {
    const q = new URLSearchParams({ date });
    if (productId) q.set("product_id", productId);
    if (fullPhone) q.set("full_phone", "true");
    return api(`/api/v1/orders/ctrip-wanle-roster?${q.toString()}`);
  },
  /** 下载 Excel（带登录态 + 租户头，所以走 fetch 取 blob 再触发下载；没权限 → 403 给明确提示） */
  downloadXlsx: async (date: string, productId?: string): Promise<void> => {
    const q = new URLSearchParams({ date, format: "xlsx" });
    if (productId) q.set("product_id", productId);
    const st = useAuthStore.getState();
    const r = await fetch(`${apiConfig.base}/api/v1/orders/ctrip-wanle-roster?${q.toString()}`, {
      headers: { ...(st.accessToken ? { Authorization: `Bearer ${st.accessToken}` } : {}), ...(st.user?.tenantSlug ? { "X-Tenant-Slug": st.user.tenantSlug } : {}) },
    });
    if (!r.ok) throw new Error(r.status === 403 ? "没有下载权限（需要「订单·携程玩乐名单（全号 / 下载）」）" : `下载失败（${r.status}）`);
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = `携程玩乐名单-${date}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  /** 绑定 / 改编号：携程产品号必填，PLU / 套餐编号不填 = 选项编号 -PAX / 选项编号（后端补） */
  bind: (body: { product_id: string; option_id: string; ctrip_product_id: string; package_code?: string; plu?: string; child_plu?: string }): Promise<unknown> =>
    api(`/api/v1/integrations/platforms/ctrip_wanle/mappings`, { method: "POST", body: JSON.stringify(body) }),
  savePriceSettings: (body: { price_push_enabled: boolean; price_mode: "cost" | "sale" | null; commission_pct: number | null }): Promise<{ ok: boolean; ready: boolean; missing: string[] }> =>
    api(`/api/v1/integrations/platforms/ctrip_wanle/wanle-price-settings`, { method: "PUT", body: JSON.stringify(body) }),
  /** WANLE-URL-1 · 只改传了的键；空串 = 清空；后端校验 https + *.ctrip.com / *.ctripqa.com（422） */
  /** WANLE-PRICE-1 · 单个选项固定价：fixed_cents = null 回到跟随 API 渠道价；低于成本必须带 note（否则 422） */
  savePrice: (mappingId: string, body: { fixed_cents: number | null; note?: string }): Promise<{ ok: boolean; changed: boolean; queued: boolean; will_push_price?: boolean }> =>
    api(`/api/v1/integrations/platforms/ctrip_wanle/mappings/${mappingId}/wanle-price`, { method: "PUT", body: JSON.stringify(body) }),
  /** WANLE-SBX-1 · 沙箱测试日期（≤5）；空数组 = 退出沙箱模式 */
  saveSandbox: (dates: string[]): Promise<{ ok: boolean; wanle_sandbox_dates: string[] }> =>
    api(`/api/v1/integrations/platforms/ctrip_wanle/wanle-sandbox`, { method: "PUT", body: JSON.stringify({ dates }) }),
  /** 沙箱手动推一个用例：携程原话照回（失败也 200）；非沙箱 409、价格配置不齐 422 */
  sandboxPush: (body: { mapping_id: string; kind: "price" | "inventory"; scope: "one" | "all" }): Promise<WanleSandboxResult> =>
    api(`/api/v1/integrations/platforms/ctrip_wanle/wanle-sandbox:push`, { method: "POST", body: JSON.stringify(body) }),
  saveUrls: (body: Partial<{ order_notice_url: string; price_url: string; stock_url: string }>): Promise<{ ok: boolean }> =>
    api(`/api/v1/integrations/platforms/ctrip_wanle/wanle-urls`, { method: "PUT", body: JSON.stringify(body) }),
  saveCredentials: (body: { account_id: string; signkey: string; aes_key: string; aes_iv: string }): Promise<{ ok: boolean; configured: boolean }> =>
    api(`/api/v1/integrations/platforms/ctrip_wanle/wanle-credentials`, { method: "PUT", body: JSON.stringify(body) }),
};
